// LiveTranscriptPanel — surfaces the rolling input/output transcript of the
// active Gemini 3.1 Live session in a floating right-anchored panel.
//
// Consumes the shared `useLiveVoice` state owned by the parent (AuthedApp),
// so no hook call here. Fades out 5 seconds after the session returns to idle.

import { useEffect, useRef, useState } from 'react';
import type { LiveVoiceState } from '../hooks/useLiveVoice';

export function LiveTranscriptPanel({
  state,
  transcript,
  outputTranscript,
  error,
}: {
  state: LiveVoiceState;
  transcript: string;
  outputTranscript: string;
  error: string | null;
}): JSX.Element | null {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (state === 'idle') {
      // Fade out 5s after returning to idle unless the user dismissed sooner.
      const t = window.setTimeout(() => setVisible(false), 5000);
      return () => window.clearTimeout(t);
    }
    // Any non-idle state re-shows the panel.
    setVisible(true);
    setDismissed(false);
  }, [state]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [transcript, outputTranscript]);

  if (!visible || dismissed) return null;

  return (
    <aside className="live-transcript-panel" role="log" aria-live="polite">
      <header>
        <span className="live-badge" aria-label="Live voice session">
          LIVE
        </span>
        <span className="muted small">{state}</span>
        <button
          className="close"
          onClick={() => setDismissed(true)}
          aria-label="Close live transcript panel"
          title="Close panel (does not stop the session)"
        >
          ×
        </button>
      </header>
      {error ? (
        <div className="status-missing small" style={{ marginBottom: 8 }}>
          {error}
        </div>
      ) : null}
      <section>
        <h4>You</h4>
        <div className="transcript-you">
          {transcript || <span className="muted">—</span>}
        </div>
      </section>
      <section>
        <h4>Assistant</h4>
        <div className="transcript-assistant">
          {outputTranscript || <span className="muted">—</span>}
        </div>
      </section>
      <div ref={bottomRef} />
    </aside>
  );
}
